import { useQuery, useMutation, useQueryClient, type UseQueryResult, type UseMutationResult } from '@tanstack/react-query';
import type { InvokeChannelMap, InvokeChannel } from '@volley/crucible-ipc-types/ipc-channels';

/**
 * React Query wrapper for typed IPC invoke calls (read operations).
 *
 * For channels that take void args: `useIpcQuery('channel')`
 * For channels with args: `useIpcQuery('channel', args)`
 * Options (enabled etc.) can be passed as the last argument.
 */
export function useIpcQuery<C extends InvokeChannel>(
  channel: C,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  args?: any,
  options?: { enabled?: boolean; staleTime?: number },
): UseQueryResult<InvokeChannelMap[C][1]> {
  // If args looks like an options object (has 'enabled' key), shift it
  const actualArgs = (args && typeof args === 'object' && 'enabled' in args) ? undefined : args;
  const actualOptions = (args && typeof args === 'object' && 'enabled' in args) ? args : options;

  return useQuery({
    queryKey: actualArgs !== undefined ? [channel, actualArgs] : [channel],
    queryFn: () => {
      if (actualArgs !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (window.crucible.invoke as any)(channel, actualArgs);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (window.crucible.invoke as any)(channel);
    },
    enabled: actualOptions?.enabled,
    staleTime: actualOptions?.staleTime,
  });
}

/**
 * React Query mutation wrapper for typed IPC invoke calls (write operations).
 *
 * Optionally invalidates query keys after a successful mutation.
 */
export function useIpcMutation<C extends InvokeChannel>(
  channel: C,
  options?: { invalidateKeys?: string[][] },
): UseMutationResult<InvokeChannelMap[C][1], Error, InvokeChannelMap[C][0]> {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (args: InvokeChannelMap[C][0]) => {
      if (args !== undefined) {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        return (window.crucible.invoke as any)(channel, args);
      }
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      return (window.crucible.invoke as any)(channel);
    },
    onSuccess: () => {
      if (options?.invalidateKeys) {
        for (const key of options.invalidateKeys) {
          queryClient.invalidateQueries({ queryKey: key });
        }
      }
    },
  });
}
